import { Button, LabeledInput } from "@apex/component-library";
import { Box, Icon, Stack, Wrap, WrapItem } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { useAccountLookupStore } from "../../hooks/useAccountLookupStore";
import { formatAccountNumber } from "../../utils/numberFormatter";

export interface MarginAccountDetailsValues {
  accountNumber?: string;
}

const HEIGHT_FOR_ERROR = "5.3rem";

interface Props {
  isFetching?: boolean;
}
export default function MarginAccountDetailsSearch({ isFetching }: Props) {
  const formik = useFormikContext<MarginAccountDetailsValues>();

  const accountNumber = useAccountLookupStore(s => s.accountNumber);
  const accountErrorMessage = useAccountLookupStore(s => s.accountErrorMessage);

  const clearAccountLookupStore = useAccountLookupStore(
    s => s.clearAccountLookupStore
  );

  const navigate = useNavigate();
  const {
    values,
    isValid,
    setFieldValue,
    handleBlur,
    touched,
    errors,
    submitForm,
    setValues,
  } = formik;

  const handleClear = () => {
    setTimeout(() => {
      navigate(`/margin-account-details`);
      clearAccountLookupStore();
      setValues({ accountNumber: "" });
    }, 0);
  };

  useEffect(() => {
    if (values.accountNumber !== accountNumber) {
      clearAccountLookupStore();
    }
  }, [accountNumber, clearAccountLookupStore, values.accountNumber]);

  const onSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setTimeout(submitForm, 0);
  };

  return (
    <Box as="form" width="100%" onSubmit={onSubmit}>
      <Wrap
        backgroundColor="apex.whiteSmoke"
        borderBottomColor="apex.darkGray"
        borderBottomWidth={1}
        align="flex-end"
        spacing="1rem"
        padding="1rem"
        width="100%"
      >
        <WrapItem h={HEIGHT_FOR_ERROR} width="input.md">
          <LabeledInput
            label="Account ID"
            name="accountNumber"
            data-testid="account-number"
            value={values.accountNumber}
            onChange={(e: { target: { value: string } }) => {
              setFieldValue(
                "accountNumber",
                formatAccountNumber(e.target.value.toUpperCase())
              );
              clearAccountLookupStore();
            }}
            onBlur={handleBlur}
            isRequired
            isInvalid={
              (touched.accountNumber && !!errors?.accountNumber) ||
              !!accountErrorMessage
            }
            errorMessage={errors?.accountNumber || accountErrorMessage}
            isDisabled={isFetching}
          />
        </WrapItem>

        <WrapItem
          h={HEIGHT_FOR_ERROR}
          width="120px"
          alignItems="center"
          paddingTop="0.85rem"
        >
          <Stack direction="row" spacing={8} alignItems="center">
            <Button
              variant="primary"
              type="submit"
              isDisabled={!isValid}
              isLoading={!!isFetching}
              data-testid="search-btn"
            >
              Search
            </Button>
            <Button variant="link" onClick={handleClear}>
              <Icon as={FaTimes} pr="5px" /> Clear Search
            </Button>
          </Stack>
        </WrapItem>
      </Wrap>
    </Box>
  );
}
